import { Component, output, signal, computed, effect, OnInit, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { TextboxComponent } from '../textbox/textbox.component';
import { ButtonComponent } from '../button/button.component';
import { AMAZON_SVG, APPLE_SVG, FACEBOOK_SVG, GOOGLE_SVG } from '../../../assets/svg-constants';
import { StoreService } from '../../../store/store.service';
import { LoyaltyService } from '../../services/loyalty.service';
import { ModalService } from '../../services/modal.service';
import { SocialAuthService } from '../../services/social-auth.service';
import { SessionManagerService } from '../../services/session-manager.service';
import { QUERY_PARAM_ATTRIBUTES, checkRecaptchaConfig } from '../../configs/common.config';
import { Md5 } from 'md5-typescript';
import { CardComponent } from '../card/card.component';
import { RecaptchaService } from '../../services/recaptcha.service';

@Component({
  selector: 'app-loyalty-sign-in',
  standalone: true,
  imports: [CommonModule, TextboxComponent, ButtonComponent, CardComponent],
  templateUrl: './loyalty-sign-in.component.html',
  styleUrl: './loyalty-sign-in.component.scss'
})
export class LoyaltySignInComponent implements OnInit {
  // SVG Constants for social login
  public socialLoginSVG = {
    GOOGLE: GOOGLE_SVG,
    FACEBOOK: FACEBOOK_SVG,
    APPLE: APPLE_SVG,
    AMAZON: AMAZON_SVG,
  };

  // Input for Loyalty signup context
  public context = input<string>();

  // Output events
  public handleCloseSidePanel = output<void>();
  public handleOpenSidePanel = output<string>();
  public handleOpenForgotPassword = output<string>();
  public handleForgotPasswordToggle = output<void>();

  // Form signals
  public usernameOrPin = signal<string>('');
  public password = signal<string>('');
  public showPassword = signal<boolean>(false);
  // Loading states - using signals from loyalty service
  public isLoginInProgress = this.loyaltySvc.isLoginInProgress;
  public isSSOLoginInProgress = this.loyaltySvc.isSSOLoginInProgress;
  public isRecaptchaInProgress = computed(() => this.recaptchaSvc.isRecaptchaInProgress());

  // Computed values
  public propInfo = computed(() => this._storeSvc.propInfo());
  public localeObj = computed(() => this._storeSvc.localeObj());
  public isExtLoyaltyEnabled = computed(() => !(this.propInfo()?.loyaltyProviderCode === 'GP'));
  public isUppercaseLabels = computed(() => this._storeSvc.appStore.isUpperCaseLabel());
  public readonly isDesktop = computed(() => this._storeSvc.res().desktop);

  // Social Auth computed signals
  public isGoogleSDKLoaded = this.socialAuthSvc.isGoogleSDKLoaded;
  public isGoogleAuthInprogress = this.socialAuthSvc.isGoogleAuthInprogress;
  public isFacebookSDKLoaded = this.socialAuthSvc.isFacebookSDKLoaded;
  public isFacebookAuthInprogress = this.socialAuthSvc.isFacebookAuthInprogress;
  public isAppleSDKLoaded = this.socialAuthSvc.isAppleSDKLoaded;
  public isAppleAuthInprogress = this.socialAuthSvc.isAppleAuthInprogress;
  public isAmazonSDKLoaded = this.socialAuthSvc.isAmazonSDKLoaded;
  public isAmazonAuthInprogress = this.socialAuthSvc.isAmazonAuthInprogress;
  public isSocialAuthEnabled = computed(() => {
    const authConfig = this.socialAuthSvc.authConfig();
    return (
      !!authConfig?.googleClientId ||
      !!authConfig?.facebookAppId ||
      !!authConfig?.appleClientId ||
      !!authConfig?.amazonClientId
    );
  });
  public isSocialAuthInProgress = computed(
    () =>
      this.socialAuthSvc.isGoogleAuthInprogress() ||
      this.socialAuthSvc.isFacebookAuthInprogress() ||
      this.socialAuthSvc.isAppleAuthInprogress() ||
      this.socialAuthSvc.isAmazonAuthInprogress(),
  );

  constructor(
    public _storeSvc: StoreService,
    private loyaltySvc: LoyaltyService,
    private modalSvc: ModalService,
    private socialAuthSvc: SocialAuthService,
    private route: ActivatedRoute,
    private recaptchaSvc: RecaptchaService,
    private sessionManagerSvc: SessionManagerService
  ) {
    // Watch for SSO trigger from header component
    effect(() => {
      if (this.loyaltySvc.shouldTriggerSSOLogin()) {
        this.loyaltySvc.shouldTriggerSSOLogin.set(false);
        const isSSO = !!this.route.snapshot.queryParams[QUERY_PARAM_ATTRIBUTES.SSO_TOKEN];
        this.handleSignIn(isSSO);
      }
    });

    // Clear the sign-in form and close the side panel once the service signals auth is complete.
    // The session timeout is started here since SessionManagerService cannot be injected into
    // LoyaltyService without creating a circular dependency.
    effect(() => {
      if (this.loyaltySvc.signInAuthComplete()) {
        this.loyaltySvc.signInAuthComplete.set(false);
        this.clearForm();
        this.handleCloseSidePanel.emit();
        this.sessionManagerSvc.startSessionTimeout();
      }
    });

    // Watch for social login success - trigger sign-up/registration flow
    effect(() => {
      const socialUser = this.socialAuthSvc.userProfile();

      if (socialUser?.accessToken && socialUser?.provider) {
        this.handleSocialSignUp(socialUser.provider, socialUser.accessToken);
      }
    });
  }

  ngOnInit(): void {
    // Initialize social auth config from store
    const propInfo = this.propInfo();
    if (propInfo?.isLoyaltyEnabled && propInfo?.socialLoginConfig) {
      this.socialAuthSvc.setAuthConfig(propInfo.socialLoginConfig);
    }
  }

  /**
   * Main sign-in handler - supports both regular login and SSO
   */
  public handleSignIn = (isSSO: boolean = false) => {
    let usernameOrPin: string | undefined;
    let password: string | undefined;
    let encryptedPWD: string | undefined;
    let auth_code: string | undefined;

    if (!isSSO) {
      usernameOrPin = this.usernameOrPin();
      password = this.password();

      // Encrypt password - use MD5 if not external loyalty
      encryptedPWD = this.isExtLoyaltyEnabled() ? password : Md5.init(password);

      // Start login loader
      this.loyaltySvc.isLoginInProgress.set(true);
    }

    if (isSSO) {
      auth_code = this.route.snapshot.queryParams[QUERY_PARAM_ATTRIBUTES.SSO_TOKEN];
      usernameOrPin = undefined;
      encryptedPWD = undefined;

      // Start SSO login loader
      this.loyaltySvc.isSSOLoginInProgress.set(true);
    }

    // Get query params for playerTokenId and accountId
    const playerTokenId = this.route.snapshot.queryParams[QUERY_PARAM_ATTRIBUTES.PLAYER_TOKEN_ID];
    const accountId = this.route.snapshot.queryParams[QUERY_PARAM_ATTRIBUTES.ACCOUNT_ID];

    // Get property codes
    const propertyCodes = this.getPropertyCodes();

    // Case 1: If playerTokenId and accountId are present in URL (direct player info call)
    if (!!playerTokenId && !!accountId) {
      this.loyaltySvc.callGetPlayerInfoAPI(accountId, propertyCodes, isSSO, playerTokenId);
    }
    // Case 2: Normal login flow - validate credentials first
    else {
      this.handleNormalLoginWithRecaptcha(propertyCodes, usernameOrPin, encryptedPWD, auth_code, isSSO);
    }
  };

  public togglePasswordVisibility(): void {
    this.showPassword.set(!this.showPassword());
  }

  /**
   * Handle normal login with reCAPTCHA verification
   */
  private handleNormalLoginWithRecaptcha(
    propertyCodes: string,
    usernameOrPin?: string,
    encryptedPWD?: string,
    auth_code?: string,
    isSSO: boolean = false
    ) {

    if (checkRecaptchaConfig(this._storeSvc.propInfo())) {
      this.recaptchaSvc.executeAndVerifyRecaptcha({
        action: 'login',
        onSuccess: () => {
          this.handleNormalLogin(propertyCodes, usernameOrPin, encryptedPWD, auth_code, isSSO);
        },
        onError: () => {
          this.loyaltySvc.isLoginInProgress.set(false);
          this.loyaltySvc.isSSOLoginInProgress.set(false);
          this.modalSvc.displayToastMessage({
            title: '',
            desc: this.localeObj().tf_4_googleRecaptcha_verificationFailed,
            type: 'error'
          });
        }
      });
    } else {
      this.handleNormalLogin(propertyCodes, usernameOrPin, encryptedPWD, auth_code, isSSO);
    }
  }

  /**
   * Handle normal login flow - validate credentials
   */
  private handleNormalLogin(
    propertyCodes: string,
    usernameOrPin?: string,
    encryptedPWD?: string,
    auth_code?: string,
    isSSO: boolean = false
  ) {
    const params = {
      propertyCodes,
      username: usernameOrPin,
      password: encryptedPWD,
      token: auth_code,
    };

    this.loyaltySvc.validateUserLoginExtLoyalty(params).subscribe((resp: any) => {
      if (resp?.status?.status_code === 1000 && resp?.data?.authorized) {
        // Handle MFA if required
        if (resp.data.mfaValidationRequired && !resp.data.mfaGenerationFailed) {
          this.handleMFARequired(resp.data.accountId, propertyCodes, usernameOrPin || '', false);
        }
        // Handle MFA generation failure - show toast, don't open modal
        else if (resp.data.mfaValidationRequired && resp.data.mfaGenerationFailed) {
          this.handleMFARequired(resp.data.accountId, propertyCodes, usernameOrPin || '', true);
        }
        // No MFA required - proceed with player info
        else if (!resp.data.mfaValidationRequired) {
          this.loyaltySvc.callGetPlayerInfoAPI(resp.data.accountId, propertyCodes, isSSO);
        }
      } else {
        this.loyaltySvc.handleAuthError(resp, isSSO);
      }
    });
  }

  /**
   * Handle MFA requirement - trigger 2FA modal via signal
   */
  private handleMFARequired
    (accountId: string, propertyCodes: string, email: string, generationFailed: boolean = false) {
    // Close the side panel
    this.handleCloseSidePanel.emit();

    // Clear form
    this.clearForm();
    this.loyaltySvc.isLoginInProgress.set(false);

    // If generation failed, show toast only (handled by header/modal)
    if (generationFailed) {
      this.modalSvc.displayToastMessage({
        title: '',
        desc: this.localeObj().tf_1_Calendar_Loyalty_errorGeneratingOTP,
        type: 'error'
      });
      return;
    }

    // Set the signal to trigger 2FA modal in header
    this.loyaltySvc.twoFaModalData.set({
      email,
      accountId,
      propertyCodes
    });
  }

  public handleKeyUp(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.handleSignIn(false);
    }
  }

  /**
   * Get property codes based on property configuration
   */
  private getPropertyCodes(): string {
    const propInfo = this.propInfo();

    // Single property or hotel query param present
    if (propInfo.singlePropertyPortal || this.route.snapshot.queryParams[QUERY_PARAM_ATTRIBUTES.HOTEL]) {
      return propInfo.propertyCode;
    }

    // TODO: Implement when multi-property service is available
    // Multi-property - would need hotel list from multi-property service
    // For now, return default property code
    return propInfo.propertyCode;
  }

  /**
   * Clear form fields
   */
  private clearForm() {
    this.usernameOrPin.set('');
    this.password.set('');
  }

  /**
   * Handle forgot password
   */
  public handleForgotPassword = () => {
    // Toggle forgot password view in parent component (for sign-in page)
    this.handleForgotPasswordToggle.emit();

    // Open forgot password modal in header (for side panel)
    this.handleOpenForgotPassword.emit(this.usernameOrPin());
  };

  /**
   * Handle sign-up side panel - opens the sign-up panel
   */
  public handleSignUpSidePanel = () => {
    this.handleOpenSidePanel.emit('LOYALTY_SIGN_UP');
  };

  /**
   * Handle username/pin change
   */
  public handleUsernameChange = (value: string) => {
    this.usernameOrPin.set(value);
  };

  /**
   * Handle password change
   */
  public handlePasswordChange = (value: string) => {
    this.password.set(value);
  };

  /**
   * Handle social login - signs in the user using the specified provider
   */
  public signInWithSocialAccount = (provider: 'GOOGLE' | 'FACEBOOK' | 'APPLE' | 'AMAZON') => {
    const socialLoginHandlers = new Map([
      ['GOOGLE', () => this.socialAuthSvc.signInWithGoogle()],
      ['FACEBOOK', () => this.socialAuthSvc.signInWithFacebook()],
      ['APPLE', () => this.socialAuthSvc.signInWithApple()],
      ['AMAZON', () => this.socialAuthSvc.signInWithAmazon()],
    ]);

    const handler = socialLoginHandlers.get(provider);
    if (typeof handler === 'function') {
      handler();
    }
  };

  /**
   * Handle social sign-up after OAuth success
   * Fetches user profile and calls sign-up API
   */
  private async handleSocialSignUp(provider: string, accessToken: string) {
    try {
      this.loyaltySvc.isSocialSignUpInProgress.set(true);

      // Fetch user profile based on provider
      let userProfile: any = null;

      switch (provider) {
        case 'GOOGLE':
          userProfile = await this.socialAuthSvc.fetchGoogleProfile(accessToken);
          break;
        case 'FACEBOOK':
          userProfile = await this.socialAuthSvc.fetchFacebookProfile(accessToken);
          break;
        case 'AMAZON':
          userProfile = await this.socialAuthSvc.fetchAmazonProfile(accessToken);
          break;
        case 'APPLE':
          // Apple returns JWT token, email is already in the token
          // For now, we'll proceed without fetching additional profile
          userProfile = { email: '', firstName: '', lastName: '' };
          break;
      }

      if (!userProfile) {
        throw new Error('Failed to fetch user profile');
      }

      // Get property codes
      const propertyCodes = this.getPropertyCodes();

      // Prepare sign-up data
      const signUpData = {
        loginMethod: provider,
        authToken: accessToken,
        email: userProfile.email,
      };

      // Call sign-up API
      this.loyaltySvc.loyaltyRegister(signUpData, propertyCodes).subscribe({
        next: (response: any) => {
          if (response?.status?.status_code === 1000 && response?.data?.authorized) {
            // Sign-up successful, now get player info
            const accountId = response?.data?.accountId;
            this.loyaltySvc.callGetPlayerInfoAPI(accountId, propertyCodes, false);

            // Show success message
            this.modalSvc.displayToastMessage({
              title: '',
              desc: this.localeObj().tf_1_Calendar_Loyalty_signUp_successTitle,
              type: 'success'
            });

            // Reset social auth data
            this.socialAuthSvc.resetAuthData();
            this.loyaltySvc.isSocialSignUpInProgress.set(false);
          } else {
            const errorCode = response?.status?.status_code;

            // Check if account already exists (common error codes: 2001, 2002, 5001)
            const accountExistsCodes = [2001, 2002, 5001];
            if (accountExistsCodes.includes(errorCode)) {
              this.modalSvc.displayToastMessage({
                title: '',
                desc: this.localeObj().tf_1_Calendar_ExtLoyalty_signUp_errorCode_1022,
                type: 'warning'
              });
            } else {
              const errorMsg = this.localeObj()[`tf_1_Calendar_ExtLoyalty_signUp_errorCode_${errorCode}`];
              this.modalSvc.displayToastMessage({
                title: '',
                desc: errorMsg,
                type: 'error'
              });
            }

            this.socialAuthSvc.resetAuthData();
            this.loyaltySvc.isSocialSignUpInProgress.set(false);
          }
        },
        error: () => {
          this.modalSvc.displayToastMessage({
            title: '',
            desc: this.localeObj().tf_1_Calendar_Loyalty_signIn_anErrorOccurredDuringSignUp,
            type: 'error'
          });

          this.socialAuthSvc.resetAuthData();
          this.loyaltySvc.isSocialSignUpInProgress.set(false);
        }
      });
    } catch (error: any) {
      console.error('Error handling social sign-up:', error);
      this.modalSvc.displayToastMessage({
        title: '',
        desc: this.localeObj().tf_1_Calendar_Loyalty_signIn_failedToRetrieveProfile,
        type: 'error'
      });

      this.socialAuthSvc.resetAuthData();
      this.loyaltySvc.isSocialSignUpInProgress.set(false);
    }
  }

  /**
   * Navigate to signup link
   */
  public navigateToSignupLink() {
    window.open(this.propInfo().loyaltySignUpSettingsInfo?.signup_link, '_blank');
  }
}
